/**
 * Authentication service for managing login state and JWT tokens.
 */

const TOKEN_KEY = 'receptari_auth_token';
const USER_KEY = 'receptari_auth_user';

export interface UserInfo {
  id: string;
  username: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
}

/**
 * Authentication service singleton.
 * Manages login state, JWT token storage, and token expiration.
 */
class AuthServiceClass {
  private token: string | null = null;
  private user: UserInfo | null = null;
  private listeners: Set<() => void> = new Set();

  constructor() {
    // Load token and user from localStorage on initialization
    this.token = localStorage.getItem(TOKEN_KEY);
    const userJson = localStorage.getItem(USER_KEY);
    if (userJson) {
      try {
        this.user = JSON.parse(userJson);
      } catch {
        this.user = null;
      }
    }
  }

  /**
   * Check if the user is authenticated.
   */
  isAuthenticated(): boolean {
    return this.token !== null && this.user !== null;
  }

  /**
   * Get the current JWT token.
   */
  getToken(): string | null {
    return this.token;
  }

  /**
   * Get the current user info.
   */
  getUser(): UserInfo | null {
    return this.user;
  }

  /**
   * Login with username and password.
   * @returns Promise that resolves to true if login was successful
   */
  async login(username: string, password: string): Promise<boolean> {
    try {
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);

      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (!response.ok) {
        return false;
      }

      const data: LoginResponse = await response.json();
      this.token = data.access_token;

      // Fetch user info
      const userResponse = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${this.token}`,
        },
      });

      if (userResponse.ok) {
        this.user = await userResponse.json();
        this.saveToStorage();
        this.notifyListeners();
        return true;
      }

      return false;
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  }

  /**
   * Logout the current user.
   */
  logout(): void {
    this.token = null;
    this.user = null;
    this.clearStorage();
    this.notifyListeners();
  }

  /**
   * Subscribe to authentication state changes.
   * @returns Unsubscribe function
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Get authorization headers for API requests.
   */
  getAuthHeaders(): Record<string, string> {
    if (this.token) {
      return {
        'Authorization': `Bearer ${this.token}`,
      };
    }
    return {};
  }

  private saveToStorage(): void {
    if (this.token) {
      localStorage.setItem(TOKEN_KEY, this.token);
    }
    if (this.user) {
      localStorage.setItem(USER_KEY, JSON.stringify(this.user));
    }
  }

  private clearStorage(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }

  private notifyListeners(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}

// Export singleton instance
export const authService = new AuthServiceClass();
